import { useCallback, useEffect, useMemo, useState } from "react";
import classes from "./Cockpit.module.css";
import LoadingSpinnerIcon from "../../icons/LoadingSpinnerIcon";
import { env } from "../../constants/env";
import { useDynamicTitle } from "../../hooks/useDynamicTitle";

const COCKPIT_API_URL = env.primeBridgeUrl;

function formatCurrency(value) {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatGain(value) {
  if (value == null) return "—";
  const abs = Math.abs(value).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
  return value >= 0 ? `+$${abs}` : `−$${abs}`;
}

function formatPercent(value) {
  if (value == null) return "—";
  const abs = Math.abs(value).toFixed(2);
  return value >= 0 ? `(+${abs}% today)` : `(−${abs}% today)`;
}

function formatLots(value) {
  if (value == null) return "—";
  return parseFloat(Number(value).toFixed(2)).toString();
}

function formatNotional(value) {
  if (value == null) return "—";
  return `$${new Intl.NumberFormat("en-US").format(value)}`;
}

function formatPnl(value) {
  if (value == null) return "—";
  const abs = Math.abs(value).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
  return value >= 0 ? `+${abs}` : `−${abs}`;
}

function formatEquityPercent(value) {
  if (value == null) return "—";
  const abs = Math.abs(value).toFixed(2);
  return value >= 0 ? `+${abs}%` : `−${abs}%`;
}

function formatBasketLots(value) {
  const sign = value >= 0 ? "+" : "−";
  return `${sign}${Math.abs(value).toFixed(1)} lots`;
}

function formatBasketNotional(value) {
  const abs = Math.abs(value);
  if (abs >= 1000000) return `$${(abs / 1000000).toFixed(2)}M`;
  if (abs >= 1000) return `$${(abs / 1000).toFixed(1)}K`;
  return `$${abs.toFixed(0)}`;
}

function formatHeatValue(value) {
  if (value == null) return "—";
  const abs = Math.abs(value);
  const sign = value >= 0 ? "+" : "−";
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}k`;
  return `${sign}${abs.toFixed(0)}`;
}

function heatClass(profit, maxAbs) {
  if (profit === 0 || maxAbs === 0) return classes.hn;
  const intensity = Math.ceil((Math.abs(profit) / maxAbs) * 5);
  const level = Math.min(Math.max(intensity, 1), 5);
  return profit > 0 ? classes[`hg${level}`] : classes[`hr${level}`];
}

const PositionsTable = ({ positions = [], openCount, loading }) => (
  <div className={`${classes.panel} ${classes.positionsPanel}`}>
    <div className={classes.panelHead}>
      <div className={classes.panelTitle}>
        <span className={classes.num}>01</span>
        <span>Positions · Per Instrument</span>
      </div>
      <span className={classes.panelTag}>{loading ? "—" : `${openCount} OPEN`}</span>
    </div>
    <table className={classes.posTable}>
      <thead>
        <tr>
          <th>Instrument</th>
          <th>Side</th>
          <th>Net Volume</th>
          <th>Notional (USD)</th>
          <th>Open P&amp;L</th>
          <th>% of Equity</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={6} className={classes.emptyRow}>Loading…</td>
          </tr>
        ) : positions.length === 0 ? (
          <tr>
            <td colSpan={6} className={classes.emptyRow}>No open positions</td>
          </tr>
        ) : (
          positions.map((position) => (
            <tr key={position.symbol}>
              <td>
                <span className={classes.sym}>
                  <span
                    className={`${classes.symChip} ${
                      position.side === "LONG" ? classes.longChip : classes.shortChip
                    }`}
                  />
                  {position.symbol}
                </span>
              </td>
              <td>
                <span
                  className={`${classes.side} ${
                    position.side === "LONG" ? classes.sideLong : classes.sideShort
                  }`}
                >
                  {position.side}
                </span>
              </td>
              <td>{formatLots(position.lots)}</td>
              <td>{formatNotional(position.notional)}</td>
              <td className={position.openPnl >= 0 ? classes.posc : classes.neg}>
                {formatPnl(position.openPnl)}
              </td>
              <td className={position.equityPct >= 0 ? classes.posc : classes.neg}>
                {formatEquityPercent(position.equityPct)}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const BasketExposure = ({ basket = [], warnings = [], loading, error }) => {
  const maxAbsNotional = useMemo(
    () => basket.reduce((max, item) => Math.max(max, Math.abs(item.notional_usd)), 0),
    [basket]
  );

  return (
    <div className={classes.panel}>
      <div className={classes.panelHead}>
        <div className={classes.panelTitle}>
          <span className={classes.num}>02</span>
          <span>Basket Currency Exposure</span>
        </div>
        <span className={classes.panelTag}>NET / LEGS</span>
      </div>

      <div className={classes.basketColHeader}>
        <span>Currency</span>
        <span>← SHORT · NET · LONG →</span>
        <span>Exposure</span>
      </div>

      {loading ? (
        <div className={classes.emptyBlock}>Loading…</div>
      ) : error ? (
        <div className={classes.errorInline}>{error}</div>
      ) : basket.length === 0 ? (
        <div className={classes.emptyBlock}>No open positions</div>
      ) : (
        <div className={classes.basketList}>
          {basket.map((item) => {
            const isLong = item.lots >= 0;
            const barPct =
              maxAbsNotional > 0
                ? Math.min((Math.abs(item.notional_usd) / maxAbsNotional) * 50, 50)
                : 0;
            const lotsLabel = `${isLong ? "+" : "−"}${Math.abs(item.lots).toFixed(1)}`;

            return (
              <div key={item.currency} className={classes.basketRow}>
                <div className={classes.ccyBadge}>{item.currency}</div>

                <div className={classes.basketBarTrack}>
                  <div className={classes.basketBarMid} />
                  <div
                    className={`${classes.basketBarFill} ${
                      isLong ? classes.basketBarLong : classes.basketBarShort
                    }`}
                    style={{ width: `${barPct}%` }}
                  />
                  <span
                    className={`${classes.basketBarLabel} ${
                      isLong ? classes.basketBarLabelLong : classes.basketBarLabelShort
                    }`}
                  >
                    {lotsLabel}
                  </span>
                </div>

                <div className={classes.basketVal}>
                  <div className={`${classes.basketLots} ${!isLong ? classes.neg : ""}`}>
                    {formatBasketLots(item.lots)}
                  </div>
                  <div className={classes.basketNotional}>
                    {formatBasketNotional(item.notional_usd)} notional
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {warnings.length > 0 && (
        <div className={classes.warningList}>
          {warnings.map((warning, index) => (
            <div key={`${warning}-${index}`} className={classes.warningItem}>
              {"⚠ "}
              {warning}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const HeatmapCard = ({ positions = [], loading }) => {
  const maxAbs = useMemo(
    () => positions.reduce((max, position) => Math.max(max, Math.abs(position.openPnl)), 0),
    [positions]
  );

  return (
    <div className={classes.panel}>
      <div className={classes.panelHead}>
        <div className={classes.panelTitle}>
          <span className={classes.num}>03</span>
          <span>P&amp;L Heatmap · Instruments</span>
        </div>
      </div>

      <div className={classes.heatGrid}>
        {loading ? (
          <div className={classes.heatEmpty}>Loading…</div>
        ) : positions.length === 0 ? (
          <div className={classes.heatEmpty}>No data</div>
        ) : (
          positions.map((position) => (
            <div
              key={position.symbol}
              className={`${classes.heatCell} ${heatClass(position.openPnl, maxAbs)}`}
            >
              <span className={classes.hsym}>{position.symbol}</span>
              <span className={classes.hval}>{formatHeatValue(position.openPnl)}</span>
            </div>
          ))
        )}
      </div>

      <div className={classes.heatLegend}>
        <span>− LOSS</span>
        <div className={classes.heatLegendSwatches}>
          <span style={{ background: "#fca5a5" }} />
          <span style={{ background: "#fecaca" }} />
          <span style={{ background: "#f4f7f9" }} />
          <span style={{ background: "#bbf7d0" }} />
          <span style={{ background: "#4ade80" }} />
        </div>
        <span>GAIN +</span>
      </div>
    </div>
  );
};

const Cockpit = () => {
  useDynamicTitle("Risk Cockpit", "dashboard/cockpit");

  const [data, setData] = useState(null);
  const [basket, setBasket] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [basketLoading, setBasketLoading] = useState(true);
  const [error, setError] = useState(null);
  const [basketError, setBasketError] = useState(null);

  const fetchCockpit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${COCKPIT_API_URL}/api/cockpit`);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      setData(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBasket = useCallback(async () => {
    setBasketLoading(true);
    setBasketError(null);
    try {
      const response = await fetch(`${COCKPIT_API_URL}/api/basket`);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const json = await response.json();
      setBasket(json.basket ?? []);
      setWarnings(json.warnings ?? []);
    } catch (err) {
      setBasketError(err.message);
    } finally {
      setBasketLoading(false);
    }
  }, []);

  const refreshAll = useCallback(() => {
    fetchCockpit();
    fetchBasket();
  }, [fetchBasket, fetchCockpit]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const cockpit = data || {};

  return (
    <div className="main-divs">
      <div className={classes.wrapper}>
        <header className={classes.mainHeader}>
          <div className={classes.headerTitles}>
            <h1>Risk Cockpit</h1>
            <div className={classes.headerMeta}>
              <div className={classes.metaPill}>
                <span className={classes.liveDot} />
                {loading ? "—" : cockpit.session ?? "—"}
              </div>
              <div className={classes.metaPill}>
                TIME{" "}
                <span className={classes.clockVal}>
                  {loading ? "--:--:--" : cockpit.brokerTime ?? "--:--:--"}
                </span>
              </div>
            </div>
          </div>
          <button
            className={classes.refreshButton}
            onClick={refreshAll}
            disabled={loading || basketLoading}
          >
            <span className={loading || basketLoading ? classes.spin : undefined}>
              <LoadingSpinnerIcon
                width="15"
                height="15"
                color="var(--primary-color)"
              />
            </span>
            {loading || basketLoading ? "Loading…" : "Refresh"}
          </button>
        </header>

        {error && <div className={classes.errorBanner}>Failed to load data: {error}</div>}

        <div className={classes.heroStats}>
          <div className={`${classes.heroCard} ${classes.accent}`}>
            <div className={classes.heroLabel}>Net Equity</div>
            <div className={classes.heroValue}>
              {loading ? "—" : formatCurrency(cockpit.netEquity)}
            </div>
            <div className={classes.heroSub}>
              {!loading && cockpit.equityGain !== undefined && (
                <>
                  <span
                    className={cockpit.equityGain >= 0 ? classes.deltaPos : classes.deltaNeg}
                  >
                    {formatGain(cockpit.equityGain)}
                  </span>{" "}
                  <span
                    className={
                      cockpit.equityGainPct >= 0 ? classes.deltaPos : classes.deltaNeg
                    }
                  >
                    {formatPercent(cockpit.equityGainPct)}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className={`${classes.heroCard} ${classes.danger}`}>
            <div className={classes.heroLabel}>Open P&amp;L</div>
            <div
              className={`${classes.heroValue} ${
                !loading && cockpit.openPnl < 0 ? classes.neg : ""
              }`}
            >
              {loading ? "—" : formatCurrency(cockpit.openPnl)}
            </div>
            <div className={classes.heroSub}>
              {loading ? "—" : `on ${cockpit.openCount ?? 0} open positions`}
            </div>
          </div>
        </div>

        <PositionsTable
          positions={cockpit.positions ?? []}
          openCount={cockpit.openCount ?? 0}
          loading={loading}
        />

        <div className={classes.bottomGrid}>
          <BasketExposure
            basket={basket}
            warnings={warnings}
            loading={basketLoading}
            error={basketError}
          />
          <HeatmapCard positions={cockpit.positions ?? []} loading={loading} />
        </div>

        <footer className={classes.legalLinks}>
          <a href="#">Legal &amp; Privacy</a>
          <a href="#">Risk Disclosure</a>
          <span className={classes.copyright}>© 2026 Prime Bridge</span>
        </footer>
      </div>
    </div>
  );
};

export default Cockpit;
